import axios from "axios"
import { VIEWED_PROPERTIES_KEY } from "../utils/constants"
import { getSource } from "../utils/utility"
import Algolia from "./Algolia"
import {
  getAdsIndex,
  getLocationsIndex,
  getDeletedAdsIndex,
  shouldFetchDeletedAds,
} from "./search/searchBackendFactory"
import { isElasticBackend, isElasticBackendClient } from "./search/isElasticBackend"

const CONTENT_URL = process.env.BASE_URL_CONTENT
const source = getSource()

const postLegionAction = async (action, payload) => {
  const resp = await axios
    .post("/api/legion", { action, ...payload })
    .then((resp) => resp)
    .catch((error) => error.response || error)

  if (resp.status == 200) {
    return "SUCCESS"
  } else {
    return "ERROR"
  }
}

export const sendListingTraffic = async (listing_ids) => {
  return postLegionAction("listing_traffic", { listing_ids, source })
}

export const sendLead = async (listingId, type, lead_type) => {
  return postLegionAction("listing_lead", { listing_id: listingId, type, lead_type, source })
}

export const sendEmailLead = async (data) => {
  return postLegionAction("listing_inquiry", {
    name: data?.name,
    email: data?.email,
    phone: data?.phone,
    message: data?.message,
    property_id: data?.property_id,
    source,
  })
}

export const sendContactUsEmail = async (data) => {
  const { email, phone, name, propertyId, message } = data
  return postLegionAction("contact_us", { name, email, phone, propertyId, message })
}

export const fetchSimilarAds = async (location_id, purpose_id, type_id, cookies = {}) => {
  try {
    const index = getAdsIndex("popular", cookies)
    const res = await index.search("", {
      hitsPerPage: 10,
      page: 1,
      filters: `location.externalID:${location_id} AND purpose:${
        purpose_id == 1 ? "for-sale" : "for-rent"
      } AND category.id:${type_id}`,
    })
    return res
  } catch (error) {
    return {
      error: true,
    }
  }
}

export const fetchViewedAds = async (adsIds) => {
  try {
    let newIds = []
    // Use unified factory (auto-detects SSR vs CSR)
    const index = getAdsIndex()
    const res = await index.search("", {
      filters: `${adsIds.map((id) => `externalID:${id}`).join(" OR ")}`,
    })
    if (res && res.hits) {
      let sortedHits = []
      let adsHash = {}
      for (var hit of res.hits) {
        adsHash[hit.externalID] = hit
      }
      for (var id of adsIds) {
        if (adsHash[id]) {
          sortedHits.push(adsHash[id])
          newIds.push(id)
        }
      }

      localStorage.setItem(VIEWED_PROPERTIES_KEY, JSON.stringify(newIds))
      return sortedHits
    }
    return []
  } catch (error) {
    return {
      error: true,
    }
  }
}

export const fetchAllCitiesLinkForPurposeType = async (purpose_id, type_id) => {
  let res = { error: true }
  try {
    res = await axios.get(
      `${CONTENT_URL}/get-all-location-links?purpose_id=${purpose_id}&location_id=1521&type_id=${type_id}&loc_key=loc1&loc_level=3`
    )
  } catch (error) {
    console.log(error)
  }
  return res
}

export const fetchAllLocationsForPurposeType = async (purpose_id, type_id, city_id) => {
  let res = { error: true }
  try {
    res = await axios.get(
      `${CONTENT_URL}/get-all-location-links?purpose_id=${purpose_id}&location_id=${city_id}&type_id=${type_id}&loc_key=loc3&loc_level=4`
    )
  } catch (error) {
    console.log(error)
  }
  return res
}

export const fetchListings = async (sort, algoliaQueryObj, queryString = "", cookies = {}) => {
  try {
    const index = getAdsIndex(!sort ? "popular" : sort, cookies)
    const res = await index.search(queryString, {
      ...algoliaQueryObj,
      hitsPerPage: 25,
    })

    // CAP: Limit ads to maximum results (configurable via env)
    const MAX_ADS = parseInt(process.env.NEXT_PUBLIC_MAX_ADS_LIMIT, 10)
    if (res.nbHits > MAX_ADS) {
      return {
        ...res,
        originalNbHits: res.nbHits, // Preserve original count for display
        nbHits: MAX_ADS,
        nbPages: Math.ceil(MAX_ADS / res.hitsPerPage),
      }
    }

    return res
  } catch (error) {
    console.error("fetchListings error:", error)
    // Return empty result instead of undefined to prevent page crash
    return { hits: [], nbHits: 0, page: 0, nbPages: 0, hitsPerPage: 25 }
  }
}

export const fetchDeletedListings = async (algoliaQueryObj, queryString = "", cookies = {}) => {
  // Deleted ads index is only available in Algolia - not used in ElasticSearch
  if (!shouldFetchDeletedAds(cookies)) {
    return { hits: [], nbHits: 0 }
  }

  try {
    const index = getDeletedAdsIndex(cookies)
    if (!index) {
      return { hits: [], nbHits: 0 }
    }
    const res = await index.search(queryString, {
      ...algoliaQueryObj,
      hitsPerPage: 25,
    })
    return res
  } catch (error) {
    console.log(error)
  }
}

export const fetchLocation = async (id, cookies = {}) => {
  try {
    // Use client-side function when in browser, server-side when on server
    // Unified factory auto-detects SSR vs CSR
    const index = getLocationsIndex(cookies)
    const res = await index.search("", {
      filters: `externalID:${id}`,
    })

    return res.hits[0]
  } catch (error) {
    console.log(error)
  }
}

/**
 * Export flag check functions for use in pages
 */
export { isElasticBackend, shouldFetchDeletedAds }

